import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useDiscover } from '../../hooks/useDiscover';
import { useNavigation } from '../../hooks/useNavigation';
import { useMessages } from '../../hooks/useMessages';
import { useWebSocket } from '../../hooks/useWebSocket';

interface MatchRequest {
  id: string;
  fromUserId?: string;
  toUserId?: string;
  fromUser?: {
    firstName: string;
    lastName: string;
    username: string;
  };
  toUser?: {
    firstName: string;
    lastName: string;
    username: string;
  };
  message?: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  createdAt: string;
  expiresAt: string;
  respondedAt?: string;
}

interface MatchRequestNotificationProps {
  onRequestUpdate?: () => void;
}

const MatchRequestNotification: React.FC<MatchRequestNotificationProps> = ({ onRequestUpdate }) => {
  const { user } = useAuth();
  const { getMatchInvitations, respondToInvitation } = useDiscover();
  const navigation = useNavigation();
  const { loadConversations } = useMessages();
  const { onMessage, offMessage, isConnected } = useWebSocket();
  const [pendingRequests, setPendingRequests] = useState<MatchRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Load pending requests
  const loadPendingRequests = React.useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await getMatchInvitations();
      if (result.success && result.data) {
        // Get only pending received requests
        // result.data is an object with 'sent' and 'received' arrays
        const data = result.data as { sent?: MatchRequest[]; received?: MatchRequest[] };
        const received = data.received || [];
        const pending = received.filter((req: MatchRequest) => req.status === 'pending');
        setPendingRequests(pending);
      }
    } catch (error) {
      console.error('Failed to load match requests:', error);
    } finally {
      setLoading(false);
    }
  }, [user, getMatchInvitations]);

  // Load requests on mount and when user changes
  useEffect(() => {
    loadPendingRequests();
    // Polling removed - now using WebSocket events for real-time updates
  }, [loadPendingRequests]);

  // Listen for WebSocket events
  useEffect(() => {
    if (!isConnected || !user) return;

    // Handle new match request
    const handleNewRequest = (data: any) => {
      // Only add if this is a request received by current user
      if (data.toUserId === user.id) {
        const newRequest: MatchRequest = {
          id: data.id,
          fromUserId: data.fromUserId,
          toUserId: data.toUserId,
          fromUser: data.fromUser,
          message: data.message,
          status: data.status || 'pending',
          createdAt: data.createdAt,
          expiresAt: data.expiresAt
        };
        setPendingRequests(prev => {
          // Check if request already exists to avoid duplicates
          const exists = prev.some(req => req.id === newRequest.id);
          if (exists) return prev;
          return [...prev, newRequest];
        });
      }
    };

    // Handle accepted match request
    const handleAcceptedRequest = (data: any) => {
      // Remove from pending list if it's a request we received
      if (data.toUserId === user.id || data.fromUserId === user.id) {
        setPendingRequests(prev => prev.filter(req => req.id !== data.id));
        
        // Refresh conversations if this user accepted
        if (data.toUserId === user.id && loadConversations) {
          loadConversations().catch(err => console.warn('Failed to refresh conversations:', err));
        }
        
        // Notify parent component
        if (onRequestUpdate) {
          onRequestUpdate();
        }
      }
    };

    // Handle declined match request
    const handleDeclinedRequest = (data: any) => {
      // Remove from pending list if it's a request we received
      if (data.toUserId === user.id || data.fromUserId === user.id) {
        setPendingRequests(prev => prev.filter(req => req.id !== data.id));
        
        // Notify parent component
        if (onRequestUpdate) {
          onRequestUpdate();
        }
      }
    };

    // Register WebSocket event listeners
    onMessage('matchRequest:new', handleNewRequest);
    onMessage('matchRequest:accepted', handleAcceptedRequest);
    onMessage('matchRequest:declined', handleDeclinedRequest);

    // Cleanup: unregister listeners
    return () => {
      offMessage('matchRequest:new');
      offMessage('matchRequest:accepted');
      offMessage('matchRequest:declined');
    };
  }, [isConnected, user, onMessage, offMessage, loadConversations, onRequestUpdate]);

  // Handle accept
  const handleAccept = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      const result = await respondToInvitation(requestId, 'accepted');
      if (result.success) {
        // Remove from pending list
        setPendingRequests(prev => prev.filter(req => req.id !== requestId));
        
        // Extract conversation ID from response
        const conversationId = result.data?.conversation?.id || result.data?.conversationId;
        
        // Refresh conversations list to show the new conversation
        if (loadConversations) {
          try {
            await loadConversations();
          } catch (err) {
            console.warn('Failed to refresh conversations:', err);
          }
        }
        
        // Notify parent component
        if (onRequestUpdate) {
          onRequestUpdate();
        }
        
        // Show success message with option to navigate to conversation
        if (conversationId) {
          const goToConversation = window.confirm(
            'Match request accepted! A conversation has been created. Would you like to go to the conversation now?'
          );
          if (goToConversation) {
            navigation.goToConversation(conversationId);
            setShowModal(false);
          }
        } else {
          alert('Match request accepted! You can now message this user from the Messages page.');
        }
      } else {
        alert(result.error || 'Failed to accept request. Please try again.');
      }
    } catch (error) {
      console.error('Failed to accept request:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle decline
  const handleDecline = async (requestId: string) => {
    if (!window.confirm('Are you sure you want to decline this match request? This user will not appear in your discover results.')) {
      return;
    }

    setProcessingId(requestId);
    try {
      const result = await respondToInvitation(requestId, 'declined');
      if (result.success) {
        // Remove from pending list
        setPendingRequests(prev => prev.filter(req => req.id !== requestId));
        // Notify parent component
        if (onRequestUpdate) {
          onRequestUpdate();
        }
        alert('Match request declined.');
      } else {
        alert(result.error || 'Failed to decline request. Please try again.');
      }
    } catch (error) {
      console.error('Failed to decline request:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const pendingCount = pendingRequests.length;

  return (
    <>
      {/* Notification Badge */}
      <button
        onClick={() => setShowModal(true)}
        className="relative bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors flex items-center space-x-2 shadow-sm"
        disabled={loading}
      >
        <span>🔔 Notifications</span>
        {pendingCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-terracotta text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border border-coffee">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </button>

      {/* Notification Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-coffee">Match Requests</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-coffee hover:text-terracotta text-2xl transition-colors"
              >
                ×
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="text-coffee">Loading requests...</div>
              </div>
            ) : pendingRequests.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-coffee text-xl mb-2">No pending requests</div>
                <div className="text-coffee opacity-80">You're all caught up!</div>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingRequests.map((request) => (
                  <div
                    key={request.id}
                    className="bg-white border-2 border-coffee rounded-lg p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-coffee font-semibold text-lg">
                          {request.fromUser?.firstName} {request.fromUser?.lastName}
                        </h3>
                        <p className="text-coffee opacity-70 text-sm">
                          @{request.fromUser?.username}
                        </p>
                        {request.message && (
                          <p className="text-coffee mt-2 text-sm bg-cream border border-coffee rounded p-2">
                            "{request.message}"
                          </p>
                        )}
                        <p className="text-coffee opacity-60 text-xs mt-2">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleAccept(request.id)}
                        disabled={processingId === request.id}
                        className="flex-1 bg-terracotta text-white px-4 py-2 rounded-lg hover:bg-[#d89050] transition-colors disabled:opacity-50 border-2 border-coffee shadow-sm"
                      >
                        {processingId === request.id ? 'Processing...' : '✓ Accept'}
                      </button>
                      <button
                        onClick={() => handleDecline(request.id)}
                        disabled={processingId === request.id}
                        className="flex-1 bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 shadow-sm"
                      >
                        {processingId === request.id ? 'Processing...' : '✗ Decline'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MatchRequestNotification;

